// Plays featured videos in a popup on the page: YouTube, Vimeo, or a direct
// .mp4/.webm file (e.g. one in /public/featured).
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export type Embed = { kind: "iframe" | "video"; src: string };

function youTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

/** How to play a video link on the page, or null to open it in a new tab. */
export function embedFor(url: string): Embed | null {
  const yt = youTubeId(url);
  if (yt) return { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&rel=0` };
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1` };
  if (/\.(mp4|webm)(\?|$)/i.test(url)) return { kind: "video", src: url };
  return null;
}

/** A still for a video: the given image, else YouTube's own thumbnail. */
export function posterFor(videoUrl?: string, image?: string): string | undefined {
  if (image) return image;
  const yt = videoUrl ? youTubeId(videoUrl) : null;
  return yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : undefined;
}

/** Plays `url` in the popup when it can be embedded, else opens a new tab.
 *  Returns the embed to pass to <VideoLightbox>, or null. */
export function playVideo(url: string): Embed | null {
  const embed = embedFor(url);
  if (!embed) window.open(url, "_blank", "noopener,noreferrer");
  return embed;
}

export function VideoLightbox({ title, embed, onClose }: { title: string; embed: Embed; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="vp-lightbox" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="vp-frame" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="vp-close" onClick={onClose} aria-label="Close video">
          <X size={20} aria-hidden />
        </button>
        {embed.kind === "iframe" ? (
          <iframe src={embed.src} title={title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
        ) : (
          <video src={embed.src} controls autoPlay />
        )}
      </div>
    </div>,
    document.body
  );
}
